import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";

import { MediaPreview } from "../../../components/dashboard/MediaPreview";
import { useConfirm } from "../../../contexts/ConfirmContext";
import { useToast } from "../../../contexts/ToastContext";
import { extractErrorMessage } from "../../../lib/api";
import {
  deleteMedia,
  listAdvertisers,
  listMedia,
  listUsers,
  uploadMedia,
} from "../../../lib/dashboardApi";
import type { UploadProgress } from "../../../lib/dashboardApi";
import { hasMinRole } from "../../../lib/hooks/useAuthMe";
import type { MediaItem, User } from "../../../types/dashboard";

type Ctx = { user?: User };

export function Media() {
  const { user } = useOutletContext<Ctx>();
  const { toast } = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const canEdit = hasMinRole(user, "editor");
  const isOwner = user?.role === "owner";

  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [advertiserFilter, setAdvertiserFilter] = useState("");
  const [uploaderFilter, setUploaderFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sort, setSort] = useState("newest");
  const [uploadAdv, setUploadAdv] = useState<string>("");
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [downloading, setDownloading] = useState(false);
  const [preview, setPreview] = useState<MediaItem | null>(null);

  const { data: advertisers = [] } = useQuery({
    queryKey: ["advertisers"],
    queryFn: listAdvertisers,
  });
  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: listUsers,
    enabled: isOwner,
  });
  const { data: media = [], isLoading } = useQuery({
    queryKey: ["media", { search, advertiserFilter, uploaderFilter, typeFilter, sort }],
    queryFn: () =>
      listMedia({
        search,
        advertiser: advertiserFilter,
        uploader: uploaderFilter,
        type: typeFilter,
        sort,
      }),
  });

  async function doUpload(files: FileList | File[]) {
    if (!canEdit) return;
    const list = Array.from(files);
    if (list.length === 0) return;
    setUploading(true);
    try {
      const advId = uploadAdv ? Number(uploadAdv) : undefined;
      await uploadMedia(list, advId, setProgress);
      qc.invalidateQueries({ queryKey: ["media"] });
      toast(`Uploaded ${list.length} file${list.length === 1 ? "" : "s"}.`);
    } catch (e) {
      toast(extractErrorMessage(e, "Upload failed."), "error");
    } finally {
      setUploading(false);
      setProgress(null);
    }
  }

  async function remove(item: MediaItem) {
    if (
      !(await confirm({
        title: "Delete media?",
        message: `Permanently delete "${item.original_name}"?`,
        danger: true,
      }))
    )
      return;
    try {
      await deleteMedia(item.id);
      qc.invalidateQueries({ queryKey: ["media"] });
      toast("Deleted.");
    } catch (e) {
      toast(extractErrorMessage(e, "Could not delete."), "error");
    }
  }

  // Selected items, intersected with what's currently visible — switching
  // filters can leave ids in the set that aren't in view; the bulk actions
  // only ever operate on items the user can actually see.
  const selectedItems = useMemo(
    () => media.filter((m) => selectedIds.has(m.id)),
    [media, selectedIds],
  );
  const selectedCount = selectedItems.length;
  const allSelected = media.length > 0 && selectedCount === media.length;

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  function toggleSelectAll() {
    setSelectedIds(allSelected ? new Set() : new Set(media.map((m) => m.id)));
  }

  async function bulkDelete() {
    if (!canEdit || selectedCount === 0) return;
    if (
      !(await confirm({
        title: "Delete media?",
        message: `Permanently delete ${selectedCount} item${selectedCount === 1 ? "" : "s"}?`,
        danger: true,
      }))
    )
      return;
    const results = await Promise.allSettled(
      selectedItems.map((m) => deleteMedia(m.id)),
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    qc.invalidateQueries({ queryKey: ["media"] });
    clearSelection();
    if (failed) {
      toast(`Deleted ${selectedCount - failed}, ${failed} failed.`, "error");
    } else {
      toast(`Deleted ${selectedCount} item${selectedCount === 1 ? "" : "s"}.`);
    }
  }

  async function bulkDownload() {
    if (selectedCount === 0) return;
    setDownloading(true);
    try {
      // Sequential: fetch each as a blob and save under its original name.
      // Same-origin /media/* so fetch is unauthenticated and CORS-free.
      for (const m of selectedItems) {
        const res = await fetch(m.url);
        if (!res.ok) throw new Error(`HTTP ${res.status} for ${m.original_name}`);
        const blob = await res.blob();
        const href = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = href;
        a.download = m.original_name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(href);
      }
      toast(`Downloaded ${selectedCount} file${selectedCount === 1 ? "" : "s"}.`);
    } catch (e) {
      toast(extractErrorMessage(e, "Download failed."), "error");
    } finally {
      setDownloading(false);
    }
  }

  const uploaders = useMemo(
    () => users.filter((u) => u.status !== "pending"),
    [users],
  );

  return (
    <>
      <h2 className="page-title">Media Library</h2>

      {canEdit && (
        <div className="card">
          <div className="card-header">
            <h3>Upload</h3>
          </div>
          <label
            className={dragOver ? "upload-zone drag-over" : "upload-zone"}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              doUpload(e.dataTransfer.files);
            }}
          >
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              disabled={uploading}
              onChange={(e) => {
                if (e.target.files) doUpload(e.target.files);
                e.target.value = "";
              }}
            />
            {uploading ? (
              <>
                <p style={{ fontSize: 20, marginBottom: 12, color: "var(--text2)" }}>
                  {progress
                    ? `Uploading ${progress.fileName}` +
                      (progress.fileCount > 1
                        ? ` (${progress.fileIndex + 1} of ${progress.fileCount})`
                        : "")
                    : "Uploading…"}
                </p>
                <div
                  className="progress-track"
                  role="progressbar"
                  aria-valuenow={progress?.percent ?? 0}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className="progress-fill"
                    style={{ width: `${progress?.percent ?? 0}%` }}
                  />
                </div>
                <p style={{ fontSize: 12, color: "var(--text2)" }}>
                  {progress?.percent ?? 0}%
                </p>
              </>
            ) : (
              <>
                <p style={{ fontSize: 20, marginBottom: 8, color: "var(--text2)" }}>
                  Drop files here or click to upload
                </p>
                <p style={{ fontSize: 12, color: "var(--text2)" }}>Images and Videos</p>
              </>
            )}
          </label>
          <div
            style={{
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <label style={{ fontSize: 13, color: "var(--text2)", whiteSpace: "nowrap" }}>
              Upload for advertiser:
            </label>
            <select
              className="form-input"
              value={uploadAdv}
              onChange={(e) => setUploadAdv(e.target.value)}
              style={{ maxWidth: 240, padding: "8px 10px", fontSize: 13 }}
            >
              <option value="">No advertiser (general)</option>
              {advertisers.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3>
            Library{" "}
            <span style={{ color: "var(--text2)", fontSize: 13 }}>
              {media.length} item{media.length === 1 ? "" : "s"}
            </span>
          </h3>
          {media.length > 0 && (
            <button
              type="button"
              className="btn btn-sm btn-outline"
              onClick={toggleSelectAll}
            >
              {allSelected ? "Deselect all" : "Select all"}
            </button>
          )}
        </div>
        <div className="sort-bar">
          <button
            type="button"
            className="btn btn-sm btn-outline"
            onClick={() => {
              if (searchOpen) {
                setSearch("");
              }
              setSearchOpen(!searchOpen);
            }}
          >
            {searchOpen ? "Close" : "Search"}
          </button>
          {searchOpen && (
            <input
              type="text"
              placeholder="Type to search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
          <label>Advertiser:</label>
          <select
            value={advertiserFilter}
            onChange={(e) => setAdvertiserFilter(e.target.value)}
          >
            <option value="">All advertisers</option>
            <option value="unassigned">Unassigned</option>
            {advertisers.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          {isOwner && (
            <>
              <label>Uploaded by:</label>
              <select
                value={uploaderFilter}
                onChange={(e) => setUploaderFilter(e.target.value)}
              >
                <option value="">Everyone</option>
                {uploaders.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </>
          )}
          <label>Type:</label>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All</option>
            <option value="image">Images</option>
            <option value="video">Videos</option>
          </select>
          <label>Sort:</label>
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="most_played">Most played</option>
            <option value="uploader">By uploader</option>
            <option value="advertiser">By advertiser</option>
          </select>
        </div>
        {selectedCount > 0 && (
          <div className="bulk-bar">
            <span>
              {selectedCount} selected
            </span>
            <div className="bulk-actions">
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={bulkDownload}
                disabled={downloading}
              >
                {downloading ? "Downloading…" : "Download"}
              </button>
              {canEdit && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={bulkDelete}
                >
                  Delete
                </button>
              )}
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={clearSelection}
              >
                Clear
              </button>
            </div>
          </div>
        )}
        {isLoading ? (
          <div className="empty">Loading…</div>
        ) : media.length === 0 ? (
          <div className="empty">No media uploaded yet.</div>
        ) : (
          <div className="media-grid">
            {media.map((m) => (
              <MediaCard
                key={m.id}
                item={m}
                canEdit={canEdit}
                selected={selectedIds.has(m.id)}
                onToggleSelect={toggleSelect}
                onPreview={setPreview}
                onDelete={remove}
              />
            ))}
          </div>
        )}
      </div>

      <MediaPreview item={preview} onClose={() => setPreview(null)} />
    </>
  );
}

function MediaCard({
  item,
  canEdit,
  selected,
  onToggleSelect,
  onPreview,
  onDelete,
}: {
  item: MediaItem;
  canEdit: boolean;
  selected: boolean;
  onToggleSelect: (id: number) => void;
  onPreview: (m: MediaItem) => void;
  onDelete: (m: MediaItem) => void;
}) {
  const isVideo = item.mime_type?.startsWith("video");
  return (
    <div
      className={selected ? "media-item selected" : "media-item"}
      title={item.original_name}
      onClick={() => onPreview(item)}
    >
      {isVideo ? (
        <video src={item.url} muted preload="metadata" />
      ) : (
        <img src={item.url} alt={item.original_name} loading="lazy" />
      )}
      <button
        type="button"
        className="select-check"
        aria-label={`Select ${item.original_name}`}
        aria-pressed={selected}
        onClick={(e) => {
          e.stopPropagation();
          onToggleSelect(item.id);
        }}
      >
        {selected ? "✓" : ""}
      </button>
      {(item.play_count ?? 0) > 0 && (
        <span className="play-count">{item.play_count}×</span>
      )}
      {canEdit && (
        <button
          type="button"
          className="delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item);
          }}
          aria-label="Delete media"
        >
          ×
        </button>
      )}
      <div className="overlay">{item.original_name}</div>
    </div>
  );
}
